import { useState, useMemo, useEffect, useRef } from 'react';
import { Calendar, Clock } from 'lucide-react';
import Modal from './ui/Modal';
import Button from './ui/Button';

const pad = (n) => String(n).padStart(2, '0');
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeToMinutes = (t) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};
const formatTime = (t) => {
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(m)} ${period}`;
};

// 08:00, 08:30, ... 19:30, 20:00 (last slot starts 20:00, ends 20:30)
const ALL_SLOTS = (() => {
  const slots = [];
  for (let h = 8; h <= 20; h++) {
    for (const m of [0, 30]) {
      if (h === 20 && m === 30) continue;
      slots.push(`${pad(h)}:${pad(m)}`);
    }
  }
  return slots;
})();

const toggleClass = (active) =>
  `inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-terra-400 focus:ring-offset-1 ${
    active
      ? 'bg-terra-400 text-white border-terra-400'
      : 'bg-white text-ink-700 border-sand-200 hover:border-terra-400'
  }`;

const slotClass = (active) =>
  `px-3 py-2 rounded-lg text-sm font-medium border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-terra-400 focus:ring-offset-1 ${
    active
      ? 'bg-terra-400 text-white border-terra-400'
      : 'bg-white text-ink-700 border-sand-200 hover:border-terra-400'
  }`;

// Date: "Today" or a future date. Time: 30-minute slots, next 4 shown inline plus a
// "see all" modal grouped by morning/afternoon/evening.
// `initialValue` (ISO string) pre-selects a date/time — used when editing an existing booking.
export default function DateTimeSlotPicker({ onChange, required, initialValue }) {
  const now = new Date();
  const todayStr = toDateStr(now);
  const tomorrowStr = toDateStr(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));

  const initial = useMemo(() => {
    if (!initialValue) return null;
    const d = new Date(initialValue);
    return Number.isNaN(d.getTime()) ? null : d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialDateStr = initial ? toDateStr(initial) : null;
  const initialTimeStr = initial ? `${pad(initial.getHours())}:${pad(initial.getMinutes())}` : null;

  const [mode, setMode] = useState(initialDateStr && initialDateStr !== todayStr ? 'custom' : 'today');
  const [customDate, setCustomDate] = useState(initialDateStr && initialDateStr !== todayStr ? initialDateStr : tomorrowStr);
  const [selectedTime, setSelectedTime] = useState(initialTimeStr);
  const [modalOpen, setModalOpen] = useState(false);

  const selectedDateStr = mode === 'today' ? todayStr : (customDate || tomorrowStr);

  const availableSlots = useMemo(() => {
    if (mode !== 'today') return ALL_SLOTS;
    const nowMinutes = now.getHours() * 60 + now.getMinutes() + 30;
    return ALL_SLOTS.filter(t => timeToMinutes(t) > nowMinutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedDateStr]);

  // A stale slot from a previous date isn't guaranteed valid — clear it whenever the date changes.
  // Skip the clear on the very first run so a pre-filled initialValue survives mount.
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      if (initial) onChange(initial.toISOString());
      return;
    }
    setSelectedTime(null);
    onChange('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateStr]);

  const selectSlot = (t) => {
    setSelectedTime(t);
    setModalOpen(false);
    const [h, m] = t.split(':').map(Number);
    const [y, mo, d] = selectedDateStr.split('-').map(Number);
    onChange(new Date(y, mo - 1, d, h, m, 0, 0).toISOString());
  };

  const inlineSlots = availableSlots.slice(0, 4);
  const groups = [
    ['Morning', availableSlots.filter(t => timeToMinutes(t) < 12 * 60)],
    ['Afternoon', availableSlots.filter(t => timeToMinutes(t) >= 12 * 60 && timeToMinutes(t) < 17 * 60)],
    ['Evening', availableSlots.filter(t => timeToMinutes(t) >= 17 * 60)],
  ];

  return (
    <div className="flex flex-col gap-3">
      <label className="text-sm font-medium text-ink-700 block">
        Service date & time{required ? '' : ' (optional)'}
      </label>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Service date">
        <button type="button" aria-pressed={mode === 'today'} onClick={() => setMode('today')} className={toggleClass(mode === 'today')}>
          Today
        </button>
        <button type="button" aria-pressed={mode === 'custom'} onClick={() => setMode('custom')} className={toggleClass(mode === 'custom')}>
          <Calendar className="w-4 h-4" /> Choose a date
        </button>
      </div>

      {mode === 'custom' && (
        <input
          type="date"
          min={tomorrowStr}
          value={customDate}
          onChange={e => setCustomDate(e.target.value)}
          aria-label="Service date"
          className="w-full sm:w-56 px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 transition-all duration-200"
        />
      )}

      {availableSlots.length === 0 ? (
        <p className="text-sm text-ink-400">No more time slots today — choose another date.</p>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Available times">
            {inlineSlots.map(t => (
              <button key={t} type="button" aria-pressed={selectedTime === t} onClick={() => selectSlot(t)} className={slotClass(selectedTime === t)}>
                {formatTime(t)}
              </button>
            ))}
            {availableSlots.length > inlineSlots.length && (
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="px-3 py-2 rounded-lg text-sm font-medium text-terra-400 hover:bg-sand-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-terra-400"
              >
                See all times
              </button>
            )}
          </div>
          {selectedTime && (
            <p className="text-xs text-ink-400 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {mode === 'today' ? 'Today' : new Date(`${selectedDateStr}T00:00:00`).toLocaleDateString('en-AE', { day: 'numeric', month: 'short' })} at {formatTime(selectedTime)}
            </p>
          )}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Choose a time"
        footer={<Button variant="secondary" onClick={() => setModalOpen(false)}>Close</Button>}
      >
        <div className="flex flex-col gap-4 max-h-96 overflow-y-auto">
          {groups.map(([groupLabel, slots]) => slots.length > 0 && (
            <div key={groupLabel}>
              <p className="text-xs font-medium text-ink-400 uppercase tracking-wide mb-2">{groupLabel}</p>
              <div className="flex flex-wrap gap-2" role="group" aria-label={`${groupLabel} times`}>
                {slots.map(t => (
                  <button key={t} type="button" aria-pressed={selectedTime === t} onClick={() => selectSlot(t)} className={slotClass(selectedTime === t)}>
                    {formatTime(t)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
